"use client";

import React, { useRef, useState } from "react";
import { Dropdown } from "@/components/ui/dropdown/Dropdown";
import Avatar from "@/components/ui/avatar/Avatar";
import GoogleMaterialIcon from "@/components/ui/GoogleMaterialIcon";
import { SignedInAsBadge } from "@/components/common/SignedInAsBadge";
import { SwitchFamilyButton } from "@/components/family/SwitchFamilyButton";
import { useAuth } from "@/context/AuthContext";
import { useUserRole } from "@/context/UserRoleContext";

/**
 * The signed-in account control on the PUBLIC program pages: one avatar button
 * that opens a menu, in place of the old "Signed in as …" line plus a separate
 * Dashboard button.
 *
 * THE TRIGGER STILL SHOWS THE NAME. The name is a security signal, not
 * decoration (#4897, see SignedInAsBadge): the phone app can sign these pages
 * in from a link, and the name is what tells a parent whose family they are
 * about to type a child's details into. An initials-only avatar would hide it
 * behind a click, so the trigger carries the name beside the initials.
 */
export interface PublicAccountUser {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

/**
 * True only when two emails name the same account (case-insensitive). The role
 * profile can briefly belong to a different account than the session — for
 * example right after the app hands a sign-in to this page — so its name, photo
 * and role colour are used only when this holds.
 */
export function isSameAccount(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const left = a?.trim().toLowerCase();
  const right = b?.trim().toLowerCase();
  return !!left && left === right;
}

function displayName(user: PublicAccountUser | null | undefined): string {
  return user?.firstName?.trim() || user?.email?.trim() || "";
}

function initials(user: PublicAccountUser): string {
  const first = user.firstName?.trim()?.[0] ?? "";
  const last = user.lastName?.trim()?.[0] ?? "";
  const fromName = (first + last).toUpperCase();
  if (fromName) return fromName;
  return (user.email?.trim()?.[0] ?? "?").toUpperCase();
}

// Dashboard pages open in a new tab, as the old Dashboard button did: a parent
// halfway through an enrolment form keeps it.
function openInDashboard(path: string) {
  window.open(path, "_blank");
}

const ROW =
  "w-full px-4 py-2.5 min-h-[44px] text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.03] flex items-center gap-2";

export interface PublicAccountMenuItemsProps {
  user: PublicAccountUser | null | undefined;
  /** Show the family switcher row (only renders for multi-family parents). */
  showFamilySwitcher?: boolean;
  onClose: () => void;
}

/**
 * The menu rows, shared by the desktop avatar menu and the catalogue's phone
 * ⋮ menu so the two cannot drift. Every button is a menuitem because both
 * hosts wrap it in the shared Dropdown, which is role="menu".
 */
export function PublicAccountMenuItems({
  user,
  showFamilySwitcher = false,
  onClose,
}: PublicAccountMenuItemsProps) {
  const { signOut } = useAuth();
  const { currentUser } = useUserRole();
  const isParent = currentUser?.userType === "parent";

  const go = (path: string) => {
    onClose();
    openInDashboard(path);
  };

  return (
    <>
      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800">
        <SignedInAsBadge user={user} wrap />
      </div>
      {showFamilySwitcher && (
        <SwitchFamilyButton variant="menu" onSwitched={onClose} />
      )}
      <button type="button" role="menuitem" className={ROW} onClick={() => go("/dashboard")}>
        <GoogleMaterialIcon name="dashboard" size="sm" />
        Go to dashboard
      </button>
      {isParent && (
        <>
          <button
            type="button"
            role="menuitem"
            className={ROW}
            onClick={() => go("/my-registrations")}
          >
            <GoogleMaterialIcon name="list_alt" size="sm" />
            My Registrations
          </button>
          <button
            type="button"
            role="menuitem"
            className={ROW}
            onClick={() => go("/dashboard/parent/schedule")}
          >
            <GoogleMaterialIcon name="calendar_month" size="sm" />
            Family Schedule
          </button>
          <button
            type="button"
            role="menuitem"
            className={ROW}
            onClick={() => go("/dashboard/billing")}
          >
            <GoogleMaterialIcon name="payment" size="sm" />
            Billing
          </button>
        </>
      )}
      <div className="border-t border-gray-100 dark:border-gray-800 mt-1 pt-1">
        <button
          type="button"
          role="menuitem"
          className={ROW}
          onClick={() => {
            onClose();
            // Back to this same page, signed out, so the visitor can keep
            // browsing programs.
            void signOut(window.location.pathname + window.location.search);
          }}
        >
          <GoogleMaterialIcon name="logout" size="sm" />
          Sign out
        </button>
      </div>
    </>
  );
}

export interface PublicAccountMenuProps {
  user: PublicAccountUser | null | undefined;
}

export function PublicAccountMenu({ user }: PublicAccountMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { currentUser } = useUserRole();
  const name = displayName(user);
  // The profile photo and role colour come from the role profile, so use them
  // only when it is the same account as the name shown — never another
  // person's photo beside this account's name.
  const sameAccount = isSameAccount(currentUser?.email, user?.email);

  if (!user || !name) return null;

  // Focus goes back to the trigger, so a keyboard user is not dropped at the
  // top of the page after Escape or after picking a row.
  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="relative flex-shrink-0 min-w-0">
      <button
        ref={triggerRef}
        type="button"
        data-testid="public-account-menu-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Account menu, signed in as ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={name}
        // `dropdown-toggle` tells the shared Dropdown this is the trigger, so a
        // second click closes the menu instead of closing and reopening it.
        className="dropdown-toggle inline-flex items-center gap-2 max-w-[45vw] sm:max-w-xs rounded-full border border-gray-300 bg-white py-1 pl-1 pr-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
      >
        {/* The same Avatar the dashboard's own account menu uses. */}
        <span aria-hidden="true" className="flex-shrink-0">
          <Avatar
            src={sameAccount ? currentUser?.avatar_url : undefined}
            alt={name}
            size="sm"
            fallbackText={initials(user)}
            userType={sameAccount ? currentUser?.userType : undefined}
          />
        </span>
        <span className="truncate">{name}</span>
        <GoogleMaterialIcon name="expand_more" size="sm" />
      </button>
      <Dropdown
        isOpen={open}
        onClose={close}
        triggerRef={triggerRef as React.RefObject<HTMLElement>}
        className="w-64 py-1"
      >
        <PublicAccountMenuItems user={user} onClose={close} />
      </Dropdown>
    </div>
  );
}

export default PublicAccountMenu;
